/**
 * Voice-sample text definitions: id → display text, for the on-screen log.
 *
 * The bundled `voice_sample_definitions.json` ships with the client. When the
 * server plays a sample id we don't recognise, we fetch the server's
 * definitions ONCE and merge them in — with local definitions taking
 * precedence, so the on-screen text always matches the bundled audio when both
 * exist (and only the server-only ids come from the server).
 */
import bundled from '../../../voice_sample_definitions.json';

// Strip comment/section keys (those starting with "_").
function clean(raw: Record<string, string>): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(raw)) if (!k.startsWith('_')) out[k] = v;
  return out;
}

const local: Record<string, string> = clean(bundled as Record<string, string>);
const merged: Record<string, string> = { ...local };

let serverFetchStarted = false;

/** Return the display text for a sample id, or undefined if still unknown. */
export function textFor(token: string): string | undefined {
  return merged[token];
}

/**
 * Ensure the server's definitions have been merged (once). Resolves after the
 * merge so callers can re-read textFor(). Local definitions keep precedence.
 */
export async function ensureServerDefinitions(): Promise<void> {
  if (serverFetchStarted) return;
  serverFetchStarted = true;
  // Imported lazily to avoid a cycle with asset.ts at module-eval time.
  const { loadJsonAsset } = await import('../asset');
  const remote = await loadJsonAsset<Record<string, string>>('voice_sample_definitions.json');
  if (!remote) return;
  for (const [k, v] of Object.entries(clean(remote))) {
    if (!(k in local)) merged[k] = v; // local wins
  }
}
